import { createSelector } from 'reselect'
import * as R from 'ramda'
import { CONFIGURATOR_STEPS } from 'constants/pickers'
import { hasParticipantsSelectionPaxCountValid } from 'domain/pricerParticipants'
import { CurrentSelection } from 'types/typescript'
import { getCurrentSelection } from './package'
import { getHasVisibleBenefitsPicker } from './pricerBenefits'
import {
  getMaxPax,
  getMinPax
} from './pricerCapacity'
import { getHasVisibleDepartureCityPicker } from './pricerDepartureCity'
import { getHasVisibleDurationPicker } from './pricerDuration'
import { getHasVisibleDepartureDatePicker } from './pricerDepartureDate'
import { getHasVisibleParticipantsPicker } from './pricerParticipants'
import { getHaveParticipantsEnoughBirthdates } from './pricerValidator'
import {
  GetCurrentConfiguratorStep,
  GetHasAtLeastOnePicker,
  GetHasMoreThanOnePicker
} from './pricer.types'

export const getCurrentConfiguratorStep: GetCurrentConfiguratorStep = createSelector(
  getCurrentSelection,
  getHasVisibleBenefitsPicker,
  getHasVisibleDepartureCityPicker,
  getHasVisibleDurationPicker,
  getHasVisibleDepartureDatePicker,
  getHasVisibleParticipantsPicker,
  getHaveParticipantsEnoughBirthdates,
  getMinPax,
  getMaxPax,
  (
    selection: CurrentSelection,
    hasVisibleBenefitsPicker,
    isDepartureCityVisible,
    isDurationVisible,
    isDepartureDateVisible,
    areParticipantsVisible,
    haveParticipantsEnoughBirthdates,
    minPax,
    maxPax
  ) => {
    if (!selection) { return CONFIGURATOR_STEPS.NO_SELECTION }
    if (isDepartureCityVisible && !selection.departureCity) { return CONFIGURATOR_STEPS.DEPARTURE_CITY }
    if (isDurationVisible && R.isNil(selection.nights)) { return CONFIGURATOR_STEPS.DURATION }
    if (isDepartureDateVisible && !selection.checkin) { return CONFIGURATOR_STEPS.DEPARTURE_DATE }
    if (
      areParticipantsVisible &&
      (!hasParticipantsSelectionPaxCountValid(minPax, maxPax)(selection.participants) || !haveParticipantsEnoughBirthdates)
    ) {
      return CONFIGURATOR_STEPS.PARTICIPANTS
    }
    if (hasVisibleBenefitsPicker && !selection.benefits) {
      return CONFIGURATOR_STEPS.BENEFITS
    }
    return CONFIGURATOR_STEPS.COMPLETED
  }
)

export const getHasAtLeastOnePicker: GetHasAtLeastOnePicker = createSelector(
  getHasVisibleDepartureCityPicker,
  getHasVisibleDurationPicker,
  getHasVisibleDepartureDatePicker,
  (
    isDepartureCityVisible: boolean,
    isDurationVisible: boolean,
    isDepartureDateVisible: boolean
  ) => (
    isDepartureCityVisible || isDurationVisible || isDepartureDateVisible
  )
)

export const getHasMoreThanOnePicker: GetHasMoreThanOnePicker = createSelector(
  getHasVisibleDepartureCityPicker,
  getHasVisibleDurationPicker,
  getHasVisibleDepartureDatePicker,
  getHasVisibleParticipantsPicker,
  getHasVisibleBenefitsPicker,
  (
    isDepartureCityVisible: boolean,
    isDurationVisible: boolean,
    isDepartureDateVisible,
    areParticipantsVisible,
    hasVisibleBenefitsPicker
  ) => (
    [
      isDepartureCityVisible,
      isDurationVisible,
      isDepartureDateVisible,
      areParticipantsVisible,
      hasVisibleBenefitsPicker
    ].filter(Boolean).length > 1
  ))
